import * as THREE from 'three';
import type { IMapSurface } from './MapSurface';

export interface MapUserPosition {
    lat: number;
    lng: number;
    heading?: number;
    accuracy?: number;
}

export interface MapUserPositionRendererConfig {
    surface: IMapSurface;
    parent: THREE.Object3D;
    dotRadiusMeters?: number;
    rimRadiusMeters?: number;
    liftMeters?: number;
    minAccuracyRadiusMeters?: number;
    maxAccuracyRadiusMeters?: number;
}

/** Shared blue location dot for curated GLB and streamed terrain maps. */
export class MapUserPositionRenderer {
    private readonly config: Required<Omit<MapUserPositionRendererConfig, 'surface' | 'parent'>>
        & Pick<MapUserPositionRendererConfig, 'surface' | 'parent'>;
    private readonly group: THREE.Group;
    private readonly accuracyRing: THREE.Mesh;
    private position: MapUserPosition | null = null;

    constructor(config: MapUserPositionRendererConfig) {
        this.config = {
            dotRadiusMeters: 6.5,
            rimRadiusMeters: 9,
            liftMeters: 6,
            minAccuracyRadiusMeters: 12,
            maxAccuracyRadiusMeters: 80,
            ...config,
        };
        const units = config.surface.worldUnitsPerMeter;
        this.group = new THREE.Group();
        this.group.name = 'UserPosition';

        const rim = new THREE.Mesh(
            new THREE.SphereGeometry(this.config.rimRadiusMeters * units, 16, 12),
            new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, depthWrite: false }),
        );
        const dot = new THREE.Mesh(
            new THREE.SphereGeometry(this.config.dotRadiusMeters * units, 16, 12),
            new THREE.MeshBasicMaterial({ color: 0x2f7cff, depthTest: false, depthWrite: false }),
        );
        this.accuracyRing = new THREE.Mesh(
            new THREE.RingGeometry(0.78, 1, 48),
            new THREE.MeshBasicMaterial({
                color: 0x5f9dff,
                transparent: true,
                opacity: 0.24,
                side: THREE.DoubleSide,
                depthTest: false,
                depthWrite: false,
            }),
        );
        this.accuracyRing.name = 'UserPositionAccuracy';
        this.accuracyRing.rotation.x = -Math.PI / 2;
        this.accuracyRing.position.y = 0.2 * units;
        dot.position.y = 0.4 * units;
        this.group.add(this.accuracyRing, rim, dot);
        this.group.renderOrder = 10000;
        this.group.visible = false;
        config.parent.add(this.group);
    }

    setPosition(position: MapUserPosition | null): boolean {
        this.position = position ? { ...position } : null;
        if (!this.position) {
            this.group.visible = false;
            return false;
        }
        const world = this.config.surface.geoToWorld(this.position.lat, this.position.lng);
        if (!world || !this.config.surface.containsWorld(world.x, world.z)) {
            this.group.visible = false;
            return false;
        }
        const groundY = this.config.surface.getWorldY(world.x, world.z);
        if (groundY === null) {
            this.group.visible = false;
            return false;
        }
        const units = this.config.surface.worldUnitsPerMeter;
        this.group.position.set(world.x, groundY + this.config.liftMeters * units, world.z);
        const accuracyMeters = THREE.MathUtils.clamp(
            this.position.accuracy ?? 8,
            this.config.minAccuracyRadiusMeters,
            this.config.maxAccuracyRadiusMeters,
        );
        this.accuracyRing.scale.setScalar(accuracyMeters * units);
        this.group.visible = true;
        return true;
    }

    refreshSurface(): boolean {
        return this.setPosition(this.position);
    }

    dispose(): void {
        this.group.removeFromParent();
        this.group.traverse(object => {
            const mesh = object as THREE.Mesh;
            mesh.geometry?.dispose?.();
            const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
            for (const material of materials) material.dispose();
        });
    }
}
